import React, { useMemo, ReactNode } from 'react';
import { Icon, withComponent, useRouter, navigate, DrawerProvider, Modal, useTranslation } from '@sms/plasma-ui';
import app from '../../config/app.json';
import * as styled from './PageWithBookEditMenu.styles';
import { Button } from '@sms/plasma-ui/lib/components/Button/Button';
import { ColorPalette, handleTextOverflow } from '@sms/journal-commons-journal-commons-ui';
import { usePageWithBookEditMenu } from './PageWithBookEditMenu.hook';

type PageWithBookEditMenuProps = {
  children: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const SUB_PATH = (window as any).plasma?.environment?.SUB_PATH ?? '/';
const PROCESSED_SUB_PATH = SUB_PATH.replace(/\/$/, '');

function prefixPath(path: string) {
  const treatedPath = path.replace(/^\//, '');
  return `${PROCESSED_SUB_PATH}/${treatedPath}`.replace(/\/\//g, '/');
}

function PageWithBookEditMenu(props: PageWithBookEditMenuProps) {
  const { children } = props;
  const { breadcrumb } = useRouter('breadcrumb');
  const { route } = useRouter('route');
  const { t } = useTranslation();
  const {
    bookData,
    handleDeleteBook,
    bookToDelete,
    setBookToDelete,
  } = usePageWithBookEditMenu();

  const title = useMemo(() => {
    const defaultSelectedKeys = breadcrumb.map((e) => e.url);

    return (
      <>
        <Modal
          centered
          visible={bookToDelete !== null}
          onCancel={() => setBookToDelete(null)}
          onOk={() => handleDeleteBook()}
          okType="primary"
          okText={t('books.page.commons.delete')}
          cancelText={t('books.page.commons.cancel')}
          closable={false}
          keyboard={false}
          maskClosable={false}
          destroyOnClose
          title={t('books.page.card.delete.modal.title')}
        >
          <p>
            {t('books.page.card.delete.modal.description')}
          </p>
        </Modal>

        <styled.Menu
          mode="horizontal"
          selectedKeys={defaultSelectedKeys}
          onClick={({ key }) => {
            if (key === "delete-book") {
              setBookToDelete({ id: Number(route.params?.bookId ?? 0), name: bookData.name });
              return;
            }
            navigate(key);
          }}
          style={{ fontSize: 16, fontWeight: 500 }}
          id="main-menu"
        >
          <>
            <styled.BookMenu key={prefixPath(`/${app.code}/books`)}>
              <Icon prefix="far" name={"chevron-left"} />
              {t("app.menu.books.edit") + " "}
              {handleTextOverflow(bookData.name, 60)}
            </styled.BookMenu>

            {bookData.bookDeletePermission && (
              <styled.SettingsMenu key={"delete-book"}>
                <styled.SettingsWrapper>
                  <Button type="primary" title={t('app.page.menu.book.delete')} icon={<Icon prefix="far" name="trash-alt" style={{ color: ColorPalette.PRIMARY }} />}
                    style={{ backgroundColor: ColorPalette.WHITE, color: ColorPalette.PRIMARY }} />
                </styled.SettingsWrapper>
              </styled.SettingsMenu>
            )}
          </>
        </styled.Menu>
      </>
    );
  }, [breadcrumb, bookData, bookToDelete, setBookToDelete]);

  return (
    <DrawerProvider>
      {title}
      {children}
    </DrawerProvider>
  );
}

export default withComponent(PageWithBookEditMenu);
